import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CommunityInvite } from '../../../models/inviteService.model';
import { createEmptyCommunityInvite } from '../../../utils/formatUtils';
import { LocalContactInfo } from '../../../models/localContactService.model';
import { ExcelColumn } from '../../../types/misc';
import { getSelectedCommunity } from '../../../hooks/community/useSelectedCommunity';
import useCommunityEffect from '../../../hooks/effects/useCommunityEffect';
import inviteService from '../../../services/community/inviteService';
import { isValidLocalContact } from '../../../utils/validationUtils';
import {
  ImportView,
  ImportViewHeader,
  SelectColumnsHeader,
  SelectColumnsView,
  ReviewContactsView,
  ReviewContactsHeader,
} from '../../components/multi-step/excel-import';
import {
  ChooseCountryHeader,
  ChooseCountryView,
} from '../../components/multi-step/excel-import/ChooseCountryView';

export default function UploadExcelPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<CommunityInvite>(createEmptyCommunityInvite());
  const [contactInfos, setContactInfos] = useState<LocalContactInfo[]>([]);
  const [columns, setColumns] = useState<ExcelColumn[]>();
  const [fileName, setFileName] = useState<string>('');
  const [isCountryMissing, setIsCountryMissing] = useState<boolean>(false);

  const community = getSelectedCommunity();

  const resetState = () => {
    setContactInfos([]);
    setColumns(undefined);
    setFileName('');
    setIsCountryMissing(false);
  };

  useCommunityEffect(() => {
    if (!community) return;
    inviteService.fetchInvite().then((res): void => setInvite(res));
  }, []);

  useEffect(() => {
    if (!columns?.length) setColumns(undefined);
  }, [columns]);

  const view = isCountryMissing
    ? 'missingCountry'
    : contactInfos.length > 0
      ? 'review'
      : columns
        ? 'selectColumns'
        : 'import';

  const handleCountryMissing = () => {
    setIsCountryMissing(true);
  };

  const handleOnSubmitFailed = () => {
    toast.warn(t('toast.warn.importLocalContacts.cancelled'));
    resetState();
  };

  return (
    <>
      {view === 'missingCountry' ? (
        <ChooseCountryView
          invite={invite}
          contactsWithMissingCountry={contactInfos}
          setLocalContacts={setContactInfos}
          onSubmitSuccessful={() => {
            setIsCountryMissing(false);
            navigate('/contacts');
          }}
          onMoreDataNeeded={() => setIsCountryMissing(false)}
          onSubmitFailed={handleOnSubmitFailed}
        />
      ) : view === 'review' ? (
        <ReviewContactsView
          localContacts={contactInfos}
          setLocalContacts={setContactInfos}
          invite={invite}
          onSubmitSuccessful={() => navigate('/contacts')}
          onSubmitFailed={handleOnSubmitFailed}
        />
      ) : columns ? (
        <SelectColumnsView
          columns={columns}
          setColumns={setColumns}
          setLocalContacts={setContactInfos}
          invite={invite}
          fileName={fileName}
          onSubmitSuccessful={() => {
            navigate('/contacts', { state: { openInviteModal: true } });
          }}
          onSubmitFailed={handleOnSubmitFailed}
          onMissingCountry={handleCountryMissing}
        />
      ) : (
        <ImportView setFileName={setFileName} setColumns={setColumns} invite={invite} />
      )}
    </>
  );
}

